import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { useSprint, useUpdateSprint, useSprintVelocity } from '../../api/sprints';
import { useTeam } from '../../api/teams';
import { useTaskList } from '../../api/tasks';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { DetailPageSkeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/components/ui/confirm-dialog';
import ErrorState from '../../components/ui/ErrorState';
import { BarChart2, CalendarDays, CheckSquare2, ClipboardList, TrendingUp, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { formatDate } from '../../lib/dates';
import type { SprintStatus, TaskDto } from '../../types/api';

const STATUS_VARIANT: Record<SprintStatus, 'green' | 'gray' | 'blue'> = {
  Planning: 'blue', Active: 'green', Completed: 'gray',
};

interface EditForm { name: string; goal: string; startDate: string; endDate: string; capacityPoints: string; showAndTellDate: string; showAndTellNotes: string; }

type TaskColumn = 'active' | 'blocked' | 'done';
const COLUMNS: TaskColumn[] = ['active', 'blocked', 'done'];
const COLUMN_LABEL: Record<TaskColumn, string> = { active: 'Active', blocked: 'Blocked', done: 'Done' };
const COLUMN_CLS: Record<TaskColumn, string> = {
  active:  'bg-blue-50 border-blue-200',
  blocked: 'bg-red-50 border-red-200',
  done:    'bg-muted/40 border-border',
};

function TaskCard({ task }: { task: TaskDto }) {
  return (
    <Link to={`/tasks/${task.id}`} className="block rounded-md border border-border bg-card p-3 text-sm shadow-sm transition-shadow hover:shadow-md">
      <p className="truncate font-medium text-foreground">{task.title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{task.points} pts · Due {formatDate(task.dueDate)}</p>
      {task.blockerReason && <p className="mt-1 truncate text-xs text-destructive">{task.blockerReason}</p>}
    </Link>
  );
}

export default function SprintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();

  const { data: sprint, isLoading, error, refetch } = useSprint(id!);
  const { data: team }     = useTeam(sprint?.teamId ?? '');
  const { data: velocity } = useSprintVelocity(id!);
  const { data: tasks }    = useTaskList({ sprintId: id });
  const updateSprint = useUpdateSprint(id!);
  const [showEdit, setShowEdit] = useState(false);
  const { confirm, dialog } = useConfirm();

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<EditForm>();

  if (isLoading)           return <DetailPageSkeleton maxW="4xl" />;
  if (error || !sprint)    return <ErrorState onRetry={refetch} />;

  const canEdit   = can('team_lead');
  const isPlanning = sprint.status === 'Planning';
  const isActive   = sprint.status === 'Active';

  const handleActivate = () =>
    updateSprint.mutate({ activate: true }, {
      onSuccess: () => toast.success('Sprint activated.'),
      onError:   (e) => applyServerErrors(e, setError),
    });

  const handleComplete = async () => {
    if (!await confirm({ title: 'Complete this sprint?', description: 'This cannot be undone.', confirmLabel: 'Complete sprint' })) return;
    updateSprint.mutate({ complete: true }, {
      onSuccess: () => toast.success('Sprint completed.'),
      onError:   (e) => applyServerErrors(e, setError),
    });
  };

  const onSubmit = handleSubmit((values) =>
    updateSprint.mutate(
      {
        name: values.name, goal: values.goal || undefined, startDate: values.startDate, endDate: values.endDate,
        capacityPoints: values.capacityPoints ? Number(values.capacityPoints) : undefined,
        showAndTellDate: values.showAndTellDate || undefined,
        showAndTellNotes: values.showAndTellNotes || undefined,
      },
      {
        onSuccess: () => { toast.success('Sprint updated.'); setShowEdit(false); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    ),
  );

  const tasksByStatus = (col: TaskColumn): TaskDto[] =>
    (tasks?.items ?? []).filter((t: TaskDto) => t.status === col);

  const deliveryPct =
    velocity && velocity.plannedPoints > 0
      ? Math.round((velocity.deliveredPoints / velocity.plannedPoints) * 100)
      : null;

  return (
    <div className="max-w-4xl">
      {dialog}
      <PageHeader
        title={sprint.name}
        actions={
          canEdit ? (
            <div className="flex gap-2">
              {isPlanning && (
                <>
                  <Button variant="secondary" size="sm" onClick={() => {
                    reset({ name: sprint.name, goal: sprint.goal ?? '', startDate: sprint.startDate, endDate: sprint.endDate, capacityPoints: sprint.capacityPoints?.toString() ?? '', showAndTellDate: sprint.showAndTellDate ?? '', showAndTellNotes: sprint.showAndTellNotes ?? '' });
                    setShowEdit((v) => !v);
                  }}>Edit</Button>
                  <Button size="sm" onClick={handleActivate} loading={updateSprint.isPending}>Activate</Button>
                </>
              )}
              {isActive && (
                <Button variant="secondary" size="sm" onClick={handleComplete} loading={updateSprint.isPending}>Complete</Button>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Meta */}
      <Card className={cn(
        'mb-5 overflow-hidden border-l-4',
        sprint.status === 'Active'    ? 'border-l-emerald-400' :
        sprint.status === 'Planning'  ? 'border-l-primary'     :
                                        'border-l-gray-300',
      )}>
        <CardContent className="p-5">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <Badge label={sprint.status} variant={STATUS_VARIANT[sprint.status]} />
            {team && (
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{team.name}</span>
            )}
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}
            </span>
          </div>
          {sprint.goal && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Goal</p>
              <p className="mt-1 text-sm text-foreground">{sprint.goal}</p>
            </div>
          )}
          {(sprint.capacityPoints !== null || sprint.showAndTellDate) && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-border pt-4 sm:grid-cols-2">
              {sprint.capacityPoints !== null && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Capacity budget</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{sprint.capacityPoints} pts</p>
                </div>
              )}
              {sprint.showAndTellDate && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Show &amp; Tell</p>
                  <p className="mt-1 text-sm text-foreground">{sprint.showAndTellDate}</p>
                  {sprint.showAndTellNotes && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{sprint.showAndTellNotes}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Velocity */}
      {velocity && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Velocity</p>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
              <div>
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-muted">
                  <BarChart2 className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold text-foreground">{velocity.plannedPoints}</p>
                <p className="mt-0.5 text-xs font-medium text-muted-foreground">Planned pts</p>
              </div>
              <div>
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                  <Zap className="h-4 w-4 text-primary" />
                </div>
                <p className="text-2xl font-bold text-primary">{velocity.deliveredPoints}</p>
                <p className="mt-0.5 text-xs font-medium text-muted-foreground">Delivered pts</p>
              </div>
              <div>
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-950/40">
                  <CheckSquare2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{velocity.doneTasks}/{velocity.totalTasks}</p>
                <p className="mt-0.5 text-xs font-medium text-muted-foreground">Tasks done</p>
              </div>
              <div>
                <div className={cn(
                  'mb-3 flex h-9 w-9 items-center justify-center rounded-xl',
                  deliveryPct !== null && deliveryPct >= 80 ? 'bg-emerald-50 dark:bg-emerald-950/40' : 'bg-amber-50 dark:bg-amber-950/40',
                )}>
                  <CalendarDays className={cn(
                    'h-4 w-4',
                    deliveryPct !== null && deliveryPct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-500',
                  )} />
                </div>
                <p className={cn(
                  'text-2xl font-bold',
                  deliveryPct !== null && deliveryPct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-500',
                )}>
                  {deliveryPct !== null ? `${deliveryPct}%` : '—'}
                </p>
                <p className="mt-0.5 text-xs font-medium text-muted-foreground">Delivery</p>
              </div>
            </div>
            {deliveryPct !== null && (
              <div className="mt-5">
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Completion</p>
                  <p className="text-xs font-medium text-foreground">{deliveryPct}%</p>
                </div>
                <div className="h-2.5 w-full rounded-full bg-muted">
                  <div className="h-2.5 rounded-full bg-primary transition-all" style={{ width: `${Math.min(deliveryPct, 100)}%` }} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Capacity bar */}
      {sprint.capacityPoints && velocity && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
              <ClipboardList className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">Capacity</p>
          </div>
          <CardContent className="p-5">
            {(() => {
              const used = velocity.plannedPoints;
              const cap  = sprint.capacityPoints!;
              const pct  = cap > 0 ? Math.min(Math.round((used / cap) * 100), 100) : 0;
              const over = used > cap;
              return (
                <>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{used} / {cap} pts planned</span>
                    <span className={cn('font-semibold', over ? 'text-destructive' : 'text-foreground')}>{pct}%{over ? ' over capacity' : ''}</span>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-muted">
                    <div className={cn('h-2.5 rounded-full transition-all', over ? 'bg-destructive' : 'bg-primary')} style={{ width: `${pct}%` }} />
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Edit form */}
      {showEdit && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Edit sprint</p>
          </div>
          <form onSubmit={onSubmit}>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input {...register('name', { required: 'Name is required' })} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Goal</Label>
                <Input {...register('goal')} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Start date</Label>
                  <Input type="date" {...register('startDate')} />
                </div>
                <div className="space-y-1.5">
                  <Label>End date</Label>
                  <Input type="date" {...register('endDate')} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Capacity (points)</Label>
                <Input type="number" min={0} placeholder="e.g. 80" {...register('capacityPoints')} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Show &amp; Tell date</Label>
                  <Input type="date" {...register('showAndTellDate')} />
                </div>
                <div className="space-y-1.5">
                  <Label>Show &amp; Tell notes</Label>
                  <Input placeholder="e.g. Feature demo with stakeholders" {...register('showAndTellNotes')} />
                </div>
              </div>
            </div>
            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={updateSprint.isPending}>Save</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => setShowEdit(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Task board */}
      <h2 className="mb-3 text-sm font-semibold text-foreground">Tasks</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        {COLUMNS.map((col) => (
          <div key={col} className={cn('rounded-lg border p-3', COLUMN_CLS[col])}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {COLUMN_LABEL[col]} ({tasksByStatus(col).length})
            </p>
            <div className="space-y-2">
              {tasksByStatus(col).map((t) => <TaskCard key={t.id} task={t} />)}
              {tasksByStatus(col).length === 0 && (
                <p className="py-2 text-center text-xs text-muted-foreground/60">None</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
